import { useAccount } from "@Light/components/page/account";
import { useLight } from "@Light/services/light";
import type {
  PlanLocation,
  PlanResponse,
  Requirements,
} from "@Light/services/lightTypes";
import { usePlan } from "@Light/utils/plan";
import { getUtilityBehaviorFromRequirements } from "@Light/utils/utility";
import { useEnrollment } from "./flow/context";

export type TermRate = {
  term: number;
  rate?: PlanResponse;
};

/**
 * The account's supported contract term-month options (at most 3, ordered by
 * ascending term length) and the term to preselect: the plan group's term when
 * it's offered, else the shortest option.
 */
export function useTermMonthOptions() {
  const { group } = usePlan();
  const groupTerm = group.term_months;
  const groupTermOptions = group.default_term_month_options;
  const accountTermOptions = useAccount().term_month_options ?? [];
  const offeredTerms = (
    accountTermOptions.length ? accountTermOptions : (groupTermOptions ?? [])
  )
    .slice(0, 3)
    .sort((a, b) => a - b);
  const defaultTerm = offeredTerms.includes(groupTerm)
    ? groupTerm
    : offeredTerms[0];
  return { defaultTerm, offeredTerms };
}

/**
 * A rate for each of the account's supported term-month options, ordered by
 * ascending term length.
 */
export function useTermRates(options?: {
  refetchOnMountOrArgChange?: boolean;
}) {
  const { requirements } = useEnrollment();
  const { offeredTerms } = useTermMonthOptions();
  const { useRequestPlansByTermQuery } = useLight();
  const plan = usePlan();
  const plan_group = plan.group.uuid;
  const terms =
    offeredTerms.length > 0 ? offeredTerms : [plan.group.term_months];

  const query = useRequestPlansByTermQuery(
    {
      ...makePlanLocationFromRequirements(requirements),
      plan_group,
      terms,
    },
    options,
  );

  const rates: TermRate[] = (query.currentData ?? [])
    .map((entry) => ({
      term: entry.term,
      rate: entry.plans ? findRate(entry.plans, plan_group) : undefined,
    }))
    .sort((a, b) => a.term - b.term);

  return { rates, query };
}

function makePlanLocationFromRequirements(
  requirements: Requirements,
): PlanLocation {
  const utilConfig = getUtilityBehaviorFromRequirements(requirements);
  if (utilConfig.serviceLocationEntry === "search") {
    const esiId = requirements.utility_account_number?.value?.value;
    if (esiId) return { esi_id: esiId };
  }

  const postalCode = requirements.service_address?.value?.postal_code;
  const utility = requirements.utility?.value?.utility.id;
  if (postalCode && utility) return { postal_code: postalCode, utility };

  throw new Error(
    "Cannot query rates without either a utility account number or a postal code and utility",
  );
}

function findRate(
  rates: PlanResponse[] | undefined,
  groupUuid: string | undefined,
): PlanResponse | undefined {
  if (!rates) return undefined;
  for (const rate of rates) {
    if (groupUuid && groupUuid === rate.group_uuid) return rate;
    if (!groupUuid && rate.is_default_for_app) return rate;
  }
  return rates[0];
}
